import React from 'react'
import { returnsClickEvent } from '@components/returns/helpers'
import Notification from '@components/ui/Notification'
import Translations from '@helpers/translations'
import { Method } from '../../ReturnsFormModal'
import ProductCardButtons from '../ProductCardButtons'
import styles from './notification.module.scss'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'

export type SetProductToReturn = (
  lineItemId: string,
  method: Method,
  step?: number
) => void

function NotificationLinks({
  returnMethod,
  setProductToReturn,
  cancelProductToReturn,
  productId
}: {
  returnMethod: Method
  setProductToReturn: SetProductToReturn
  cancelProductToReturn: (id: string) => void
  productId: string
}) {
  const cancelReturn = () => cancelProductToReturn(productId)
  const linksConfig =
    {
      exchange: [
        {
          text: Translations.localise('pages.returns.edit'),
          action: () => setProductToReturn(productId, 'exchange')
        },
        {
          text: Translations.localise('pages.returns.cancel'),
          action: cancelReturn
        }
      ],
      refund: [
        {
          text: Translations.localise('pages.returns.edit'),
          action: () => setProductToReturn(productId, 'refund')
        },
        { text: 'Cancel', action: cancelReturn }
      ]
    }[returnMethod as Method] || []

  return (
    <div className={styles.notification__links}>
      {linksConfig.map(link => (
        <ButtonMinimal
          onClick={() => {
            returnsClickEvent({
              label: link.text
            })
            link.action()
          }}
          key={link.text}
          variant="white"
          textSize="regular"
          underlined={true}
        >
          {link.text}
        </ButtonMinimal>
      ))}
    </div>
  )
}

export type ProductCardFooterProps = {
  product: {
    productId: string
    isReturnable: boolean
    isExchangeable: boolean
    productType: string
    isMTO: boolean
  }
  returnMethod: Method | undefined
  isReturnProcessed: boolean
  setProductToReturn: SetProductToReturn
  cancelProductToReturn: (id: string) => void
  isExpired: boolean
}

const getNotReturnableNotificationText = (productType: string) => {
  if (productType === 'Trunks') {
    return 'Trunks cannot be returned due to hygiene reasons. However, if they are faulty or unopened, please contact our customer service team.'
  }

  if (productType === 'Lisle Cotton Socks') {
    return 'Socks cannot be returned due to hygiene reasons. However, if they are faulty or unopened, please contact our customer service team.'
  }

  return `${productType} cannot be returned`
}

function ProductCardFooter({
  returnMethod,
  product,
  isReturnProcessed,
  setProductToReturn,
  cancelProductToReturn,
  isExpired
}: ProductCardFooterProps) {
  const { isReturnable, productType, productId } = product

  if (isReturnable === false) {
    return (
      <Notification
        classes={styles.returns__notification}
        content={getNotReturnableNotificationText(productType)}
      />
    )
  }

  if (!isReturnProcessed && returnMethod) {
    return (
      <Notification
        classes={`${styles.returns__notification} ${styles.withLinks}`}
        type="success"
        theme="dark"
      >
        <span>{`Product updated for ${returnMethod}`}</span>
        <NotificationLinks
          cancelProductToReturn={cancelProductToReturn}
          setProductToReturn={setProductToReturn}
          returnMethod={returnMethod}
          productId={productId}
        />
      </Notification>
    )
  }

  if (isReturnProcessed && returnMethod) {
    return (
      <Notification
        classes={`${styles.returns__notification} ${styles.withLinks}`}
        type="success"
        content={`Product requested for ${returnMethod}`}
      />
    )
  }
  return (
    <ProductCardButtons
      expired={isExpired}
      id={productId}
      isMTO={product.isMTO}
      setProductToReturn={setProductToReturn}
    />
  )
}

export default ProductCardFooter
